import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchMyCommissions } from "./thunks";
import { toast } from "react-toastify";

interface Commission {
  id: number;
  type: "service" | "product" | "subscription";
  description: string;
  commission_amount: number;
  status: "pending" | "paid";
  created_at: string;
  paid_at?: string;
}

interface CommissionsSummary {
  service_commissions: number;
  product_commissions: number;
  subscription_commissions: number;
  package_commissions?: number;
  vouchers_pending?: number;
  vouchers_paid?: number;
  extras_pending?: number;
  extras_paid?: number;
  total_pending: number;
  total_paid: number;
}

interface CommissionsResponse {
  data: Commission[];
  total: number;
  current_page: number;
  per_page: number;
  last_page: number;
  from: number;
  to: number;
}

interface MyCommissionsState {
  commissions: CommissionsResponse;
  summary: CommissionsSummary;
  loading: boolean;
  error: string | null;
}

const initialState: MyCommissionsState = {
  commissions: {
    data: [],
    total: 0,
    current_page: 1,
    per_page: 10,
    last_page: 1,
    from: 0,
    to: 0,
  },
  summary: {
    service_commissions: 0,
    product_commissions: 0,
    subscription_commissions: 0,
    package_commissions: 0,
    vouchers_pending: 0,
    vouchers_paid: 0,
    extras_pending: 0,
    extras_paid: 0,
    total_pending: 0,
    total_paid: 0,
  },
  loading: false,
  error: null,
};

const myCommissionsSlice = createSlice({
  name: "myCommissions",
  initialState,
  reducers: {
    clearMyCommissions: (state) => {
      state.commissions = initialState.commissions;
      state.summary = initialState.summary;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyCommissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchMyCommissions.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          state.commissions = payload.data.data;
          state.summary = payload.data.summary;
        },
      )
      .addCase(fetchMyCommissions.rejected, (state, action) => {
        const payload = action.payload as {
          message: string;
          errors?: any;
        };
        state.error = payload?.message || "Erro ao buscar comissões";
        state.loading = false;
        if (payload?.errors && Object.keys(payload.errors).length > 0) {
          const firstErrorKey = Object.keys(payload.errors)[0];
          const firstError = payload.errors[firstErrorKey][0];
          toast.error(firstError || "Erro de validação nos filtros.");
        } else {
          toast.error(
            payload?.message || "Erro ao buscar comissões. Tente novamente.",
          );
        }
      });
  },
});

export const { clearMyCommissions } = myCommissionsSlice.actions;
export default myCommissionsSlice.reducer;
